import { z } from 'zod';
import { esquemaContrasena, esquemaCorreo } from './auth';
import { CLAVES_ROL } from './comunes';

/**
 * M1/M3 — Usuarios.
 *
 * ── El hueco que cierra este módulo ──
 * El rol `admin_empresa` existe desde la primera migración, con permisos
 * asignados, y **nadie podía llegar a él**: el autorregistro (D-022) solo produce
 * trabajadores. Un rol inalcanzable no protege nada, solo aparenta.
 */

export const ESTADOS_USUARIO = ['sin_activar', 'activo', 'desactivado'] as const;
export type EstadoUsuario = (typeof ESTADOS_USUARIO)[number];

/**
 * Roles que se pueden crear desde este módulo.
 *
 * ⚠️ **`trabajador` no está**, y no es un olvido. El trabajador entra por su
 * propia puerta: se registra con el código de su empresa y Copathy lo aprueba
 * (D-022, D-023). Crear trabajadores a mano por acá abriría una segunda vía que
 * se salta la verificación de correo y la cola —o sea, el control de acceso
 * entero— sin que se note que existen dos caminos.
 */
export const ROLES_ASIGNABLES = ['admin_copathy', 'consultor_copathy', 'admin_empresa'] as const;
export type RolAsignable = (typeof ROLES_ASIGNABLES)[number];

export const esquemaCrearUsuario = z.object({
  nombre: z
    .string()
    .trim()
    .min(3, 'Escribe el nombre completo.')
    .max(120, 'El nombre es demasiado largo.'),
  correo: esquemaCorreo,
  rol: z.enum(ROLES_ASIGNABLES, {
    errorMap: () => ({ message: 'Elige un rol de la lista.' }),
  }),
  /**
   * A qué empresa pertenece.
   *
   * ⚠️ Acá **sí** viaja un `empresa_id` desde el cliente, y es la única excepción
   * a la regla de que sale del token: Copathy está creando una cuenta para OTRA
   * empresa, así que su propio token no puede decir cuál. Lo que sostiene la
   * regla en este caso es el permiso `usuarios.administrar` —que solo tiene
   * Copathy— y el trigger `tg_usuarios_ambito_rol`, que en el motor impide que un
   * rol de ámbito Copathy caiga en una empresa cliente.
   */
  empresaId: z.number().int().positive(),
});
export type EntradaCrearUsuario = z.infer<typeof esquemaCrearUsuario>;

/**
 * Edición. El **correo no se cambia**: es la identidad con la que se entra y con
 * la que se recibió la invitación. Cambiarlo es dar de alta otra cuenta y
 * desactivar esta, que además deja el rastro correcto.
 *
 * La **empresa tampoco**: mover a alguien de empresa cambiaría de dueño todo lo
 * que ya respondió.
 */
export const esquemaEditarUsuario = z
  .object({
    nombre: z.string().trim().min(3).max(120).optional(),
    rol: z.enum(ROLES_ASIGNABLES).optional(),
    activo: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'No enviaste ningún cambio.');
export type EntradaEditarUsuario = z.infer<typeof esquemaEditarUsuario>;

export const esquemaUsuario = z.object({
  id: z.number().int().positive(),
  nombre: z.string(),
  correo: z.string(),
  rol: z.enum(CLAVES_ROL),
  rolNombre: z.string(),
  empresaId: z.number().int().positive(),
  empresaNombre: z.string(),
  estado: z.enum(ESTADOS_USUARIO),
  /** `true` si tiene una invitación viva sin usar. */
  invitacionPendiente: z.boolean(),
  ultimoAccesoEn: z.string().nullable(),
  creadoEn: z.string(),
});
export type Usuario = z.infer<typeof esquemaUsuario>;

const listaCsv = (max: number) =>
  z
    .string()
    .optional()
    .transform((v) => {
      const partes = (v ?? '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .slice(0, max);
      return partes.length ? partes : undefined;
    });

/**
 * Los dos mundos de la pantalla de usuarios.
 *
 * ── Por qué se separan ──
 * El **personal de Copathy** son unos pocos que administran la plataforma; los
 * **usuarios de clientes** son muchos, llegan solos por autorregistro y se agrupan
 * por cliente. Mezclarlos en una lista obliga a filtrar siempre para ver
 * cualquiera de los dos.
 *
 * ⚠️ **No es un campo nuevo**: se deriva de `roles.ambito`. Un ámbito `copathy` es
 * personal de la casa; `empresa` y `trabajador` son de un cliente. Guardarlo
 * aparte crearía una segunda fuente de verdad que puede contradecir al ámbito —
 * y el ámbito es lo que sostiene la frontera entre empresas.
 */
export const AMBITOS_USUARIO = ['copathy', 'cliente'] as const;
export type AmbitoUsuario = (typeof AMBITOS_USUARIO)[number];

/** Fecha ISO (2026-08-24), como la entrega un input de tipo date. */
const esquemaFecha = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'La fecha no tiene un formato válido.')
  .optional();

export const esquemaFiltroUsuarios = z.object({
  texto: z.string().trim().max(160).optional(),
  ambito: z.enum(AMBITOS_USUARIO).optional(),
  rol: listaCsv(10),
  estado: listaCsv(5),
  empresaId: z.coerce.number().int().positive().optional(),
  /** Cuándo se creó la cuenta. Rango inclusivo por los dos lados. */
  registroDesde: esquemaFecha,
  registroHasta: esquemaFecha,
  /** Última vez que entró. Sirve sobre todo para encontrar a quien NUNCA entró. */
  accesoDesde: esquemaFecha,
  accesoHasta: esquemaFecha,
  /** 1 = solo los que tienen una invitación viva sin usar. */
  invitacionPendiente: z.enum(['1']).optional(),
  pagina: z.coerce.number().int().min(1).default(1),
  porPagina: z.coerce.number().int().min(1).max(200).default(50),
});
export type FiltroUsuarios = z.infer<typeof esquemaFiltroUsuarios>;

/** Lo que devuelve crear o reenviar una invitación. */
export const esquemaInvitacion = z.object({
  usuario: esquemaUsuario,
  /** Solo en dev: el enlace que iría por correo, para poder probar sin SMTP. */
  enlaceDev: z.string().optional(),
});
export type RespuestaInvitacion = z.infer<typeof esquemaInvitacion>;

/**
 * Activación: la persona abre el enlace y elige su contraseña.
 *
 * La contraseña **la elige quien la va a usar, y nadie más la ve nunca**. Por eso
 * no se crea con una provisional: una contraseña que otro eligió tiene que viajar
 * por correo o por WhatsApp, y a partir de ahí vive en un sitio que no
 * controlamos.
 */
export const esquemaActivarCuenta = z.object({
  token: z.string().min(20).max(200),
  contrasena: esquemaContrasena,
});
export type EntradaActivarCuenta = z.infer<typeof esquemaActivarCuenta>;

export const esquemaRespuestaActivacion = z.object({
  nombre: z.string(),
  correo: z.string(),
  empresaNombre: z.string(),
});
export type RespuestaActivacion = z.infer<typeof esquemaRespuestaActivacion>;

/**
 * Reenvío masivo de invitaciones.
 *
 * ── Por qué masivo ──
 * Sacar «Reenviar invitación» de cada fila deja la tabla alineada y limpia, pero
 * invitar a diez personas serían diez visitas al perfil. Se marcan y se reenvía a
 * todas de una vez.
 *
 * El tope de 50 no es capricho: cada reenvío manda un correo, y una petición que
 * dispara cientos se convierte en una forma cómoda de hacerse bloquear por el
 * proveedor de correo.
 */
export const esquemaReenvioMasivo = z.object({
  ids: z.array(z.number().int().positive()).min(1, 'No elegiste a nadie.').max(50),
});
export type EntradaReenvioMasivo = z.infer<typeof esquemaReenvioMasivo>;

export const esquemaResultadoReenvio = z.object({
  enviadas: z.number().int(),
  /**
   * Las que no se pudieron, **con el motivo**.
   *
   * Un reenvío masivo que dice «listo» y dejó tres afuera es peor que uno que
   * falla entero: nadie va a ir a revisar cuáles quedaron, y esas tres personas
   * siguen esperando un correo que no llegó.
   */
  fallidas: z.array(z.object({ id: z.number().int(), nombre: z.string(), motivo: z.string() })),
  /** Solo en dev, mientras no haya SMTP. */
  enlacesDev: z.array(z.object({ nombre: z.string(), enlace: z.string() })).optional(),
});
export type ResultadoReenvio = z.infer<typeof esquemaResultadoReenvio>;
